import { PatientListDto, NumberSource, SortDirection } from '@lmt/shared-types';
import { MouseButton } from '@lmt/unicorn-shared/app/components/common/types';
import {
  Button,
  ButtonColor,
  ButtonWidth,
} from '@lmt/unicorn-shared/app/components/common/ui-kit/buttons/Button';
import { UniLink } from '@lmt/unicorn-shared/app/components/common/ui-kit/buttons/UniLink';
import { CheckBox } from '@lmt/unicorn-shared/app/components/common/ui-kit/CheckBox';
import {
  getFormattedDateRange,
  getFormattedDateTime,
  isMiniscreen,
  isStartMiniscreenOfflineModeEnabled,
  isStartMiniscreenOnlineModeEnabled,
  MiniscreenMode,
  openWithMiniscreen,
} from '@lmt/unicorn-shared/app/components/patients/common';
import { ExternalPatientNumberBadge } from '@lmt/unicorn-shared/app/components/patients/ExternalPatientNumberBadge';
import { MergeContext } from '@lmt/unicorn-shared/app/components/search/types';
import { useClaims } from '@lmt/unicorn-shared/app/features/auth/operations';
import ListIcon from '@lmt/unicorn-shared/app/icons/list.svg?react';
import NewPatientIcon from '@lmt/unicorn-shared/app/icons/new-patient.svg?react';
import { useScopedTranslation } from '@lmt/unicorn-shared/app/helpers/localization/useScopedTranslation';
import { nameofFactory } from '@lmt/unicorn-shared/app/helpers/nameof';
import { localizeSexType } from '@lmt/unicorn-shared/app/helpers/localization/translations';
import { useWindowSize } from 'react-use';

import {
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
} from '@mui/material';
import { snakeCase } from 'lodash';
import React, { FC, useCallback, useEffect, useRef, useState } from 'react';
import { useContextSelector } from 'use-context-selector';
import { ScrollToTop } from '../../../common/table/helper';
import { Sorting } from '../../../common/table/types';
import styles from './PatientsTable.module.scss';
import { TableDotMenu } from '../../../common/table/TableDotMenu';
import { useSubtenantsDict } from '@lmt/unicorn-shared/app/features/subtenants/selectors';
import { getFullName } from '../../patient-form/common/common';

export const defaultSorting: Sorting = {
  field: 'Id',
  direction: SortDirection.Desc,
};

export interface PatientName {
  firstName: string;
  lastName: string;
  code: string;
}

const PatientTableKeys: { [dictionary: string]: string } = {
  isSelected: 'isSelected',
  number: 'number',
  sex: 'sex',
  name: 'name',
  dateOfBirth: 'dateOfBirth',
  contact: 'contact',
  status: 'status',
  isDeleted: 'isDeleted',
  lastVisit: 'lastVisit',
  options: 'options',
};

const allColumnKeys = [
  PatientTableKeys.number,
  PatientTableKeys.sex,
  PatientTableKeys.name,
  PatientTableKeys.dateOfBirth,
  PatientTableKeys.contact,
  PatientTableKeys.status,
  PatientTableKeys.lastVisit,
];

type Props = {
  isNoPatientsFound: boolean;
  isFiltered: boolean;

  data: PatientListDto[];
  openPatientProfile: (id: number) => void;
  createPatientProfileTab: (id: number) => void;
  removePatient: (patientId: number) => void;
  anonymizePatient: (patientId: number, patientName: string) => void;
  removePatientPermanently: (patientId: number, patientName: string) => void;
  restorePatient: (patientId: number) => void;
  createDocument: (patientId: number) => void;
  changeSorting: (sorting: { sortBy?: string; desc?: boolean }) => void;
  createPatient: () => void;
};
const nameof = nameofFactory<PatientListDto>();
const sortFieldDict = {
  [PatientTableKeys.name]: nameof('lastName'),
  [PatientTableKeys.lastVisit]: nameof('lastVisitDateTime'),
};

const columnStyles = {
  [PatientTableKeys.id]: {
    minWidth: '60px',
    width: '8%',
  },
  [PatientTableKeys.sex]: {
    minWidth: '60px',
    width: '8%',
  },
  [PatientTableKeys.name]: {
    minWidth: '180px',
    width: '25%',
  },
  [PatientTableKeys.dateOfBirth]: {
    minWidth: '80px',
    width: '18%',
  },
  [PatientTableKeys.contact]: {
    minWidth: '110px',
    width: '18%',
  },
  [PatientTableKeys.status]: {
    minWidth: '80px',
    width: '12%',
  },
  [PatientTableKeys.isDeleted]: {
    minWidth: '80px',
    width: '8%',
  },
  [PatientTableKeys.lastVisit]: {
    minWidth: '120px',
    width: '15%',
  },
};

export const PatientsTable: FC<Props> = React.memo(
  function PatientsTable(props) {
    const claims = useClaims();
    const i18next = useScopedTranslation('patients.list');
    const subtenants = useSubtenantsDict();
    const isSelectable = useContextSelector(
      MergeContext,
      (v) => v.isSelectable,
    );

    const isMergeEnabled = useContextSelector(
      MergeContext,
      (v) => v.isMergeEnabled,
    );

    const setMergeEnabled = useContextSelector(
      MergeContext,
      (v) => v.setMergeEnabled,
    );

    const setMergePair = useContextSelector(
      MergeContext,
      (v) => v.setMergePair,
    );

    const { width: windowWidth } = useWindowSize();

    const [columnKeys, setColumnKeys] = useState(allColumnKeys);

    useEffect(() => {
      let newColumns = allColumnKeys;
      if (
        windowWidth > 1450 &&
        (claims.permissions.patientDeleteAndRestore ||
          claims.permissions.patientAnonymizeAndDeletePermanently)
      ) {
        newColumns = newColumns.concat([PatientTableKeys.isDeleted]);
      }
      if (windowWidth <= 1360) {
        newColumns = newColumns.filter((x) => x != PatientTableKeys.lastVisit);
      }
      if (windowWidth <= 1300) {
        newColumns = newColumns.filter((x) => x != PatientTableKeys.status);
      }
      setColumnKeys(newColumns);
    }, [windowWidth <= 1360, windowWidth > 1450, windowWidth <= 1300]);

    const [sortField, setSortField] = useState<string | null>(null);
    const [sortDirection, setSortDirection] = useState<SortDirection | null>(
      null,
    );
    const [selectedPatients, setSelectedPatients] = useState<number[]>([]);
    const [selectedSubtenantId, setSelectedSubtenantId] = useState<
      number | null
    >(null);

    const tableRef = useRef<HTMLDivElement>(null);
    useEffect(() => {
      if (tableRef.current !== null) {
        ScrollToTop(tableRef.current);
      }
    }, [props.data]);

    useEffect(() => {
      if (selectedPatients.length === 2) {
        setMergePair({
          firstPatientId: selectedPatients[0],
          secondPatientId: selectedPatients[1],
        });
      } else {
        setMergePair(null);
      }
      if (selectedPatients.length === 1) {
        const subtenantId = props.data.find(
          (patient) => patient.id === selectedPatients[0],
        )?.subtenantId;
        setSelectedSubtenantId(subtenantId ?? null);
      }
      if (selectedPatients.length === 0) {
        setSelectedSubtenantId(null);
      }
    }, [selectedPatients]);

    const checkSelectedPatient = useCallback(
      (index: 0 | 1) => {
        if (selectedPatients[index]) {
          const selectedPatient = props.data.find(
            (patient) => patient.id === selectedPatients[index],
          );
          if (!selectedPatient) {
            setSelectedPatients(
              selectedPatients.filter(
                (patientId) => patientId !== selectedPatients[index],
              ),
            );
          }
        }
      },
      [selectedPatients, props.data],
    );

    useEffect(() => {
      checkSelectedPatient(0);
      checkSelectedPatient(1);
    }, [props.data]);

    function changeSorting(field: string) {
      if (field === sortField) {
        if (sortDirection === SortDirection.Asc) {
          props.changeSorting({
            sortBy: sortFieldDict[field],
            desc: true,
          });
          setSortDirection(SortDirection.Desc);
        } else {
          props.changeSorting({
            desc: undefined,
            sortBy: undefined,
          });
          setSortField(null);
          setSortDirection(null);
        }
      } else {
        props.changeSorting({
          sortBy: sortFieldDict[field],
          desc: false,
        });
        setSortField(field);
        setSortDirection(SortDirection.Asc);
      }
    }

    const isSelectedOnChange = useCallback(() => {
      if (isMergeEnabled) {
        setMergeEnabled(false);
        setSelectedPatients([]);
      } else {
        setMergeEnabled(true);
      }
    }, [isMergeEnabled]);

    if (props.isNoPatientsFound) {
      return (
        <div className={styles.emptyList}>
          <div className={styles.emptyListText}>
            {props.isFiltered ? i18next.t('no_results') : i18next.t('empty')}
          </div>
          {!props.isFiltered && (
            <Button
              testId={'add-first-patient-button'}
              iconComponent={<NewPatientIcon />}
              color={ButtonColor.Default}
              width={ButtonWidth.Content}
              title={i18next.t('add_button')}
              onClick={props.createPatient}
            />
          )}
        </div>
      );
    }

    return (
      <TableContainer
        ref={tableRef}
        className={styles.tableContainer}
        component={Paper}
      >
        <Table
          stickyHeader
          className={styles.table}
          data-test-id="patientsTable"
        >
          <TableHead>
            <TableRow>
              {isMergeEnabled && (
                <TableCell
                  className={styles.header}
                  style={{ width: 20 }}
                  key={PatientTableKeys.isSelected}
                ></TableCell>
              )}
              {columnKeys.map((columnKey) => (
                <TableCell
                  key={`${columnKey}`}
                  onClick={() =>
                    sortFieldDict[columnKey] && changeSorting(columnKey)
                  }
                  className={
                    sortFieldDict[columnKey]
                      ? styles.sortableHeader
                      : styles.header
                  }
                  style={columnStyles[columnKey]}
                >
                  <TableSortLabel
                    active={sortField === columnKey}
                    direction={
                      sortField === columnKey
                        ? sortDirection === SortDirection.Asc
                          ? 'asc'
                          : 'desc'
                        : undefined
                    }
                    disabled={!sortFieldDict[columnKey]}
                    style={{ userSelect: 'none' }}
                    className={styles.headerCell}
                  >
                    {i18next.t(`columns.${snakeCase(columnKey)}` as any)}
                  </TableSortLabel>
                </TableCell>
              ))}

              <TableCell
                className={styles.header}
                style={
                  claims.permissions.mergePatients && isSelectable
                    ? { minWidth: 70 }
                    : { width: 20 }
                }
                key={PatientTableKeys.options}
              >
                {claims.permissions.mergePatients && isSelectable && (
                  <div className={styles.optionHeader}>
                    <UniLink
                      icon={isMergeEnabled ? undefined : <ListIcon />}
                      title={
                        isMergeEnabled
                          ? i18next.t('merge_enabled.done_button')
                          : i18next.t('merge_enabled.select_button')
                      }
                      onClick={() => isSelectedOnChange()}
                      color={ButtonColor.Primary}
                    />
                  </div>
                )}
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {props.data.map((patient) => (
              <TableRow
                key={patient.id}
                hover
                onClick={() => props.openPatientProfile(patient.id)}
                onMouseDown={(e: any) => {
                  if (e.button === MouseButton.Middle) {
                    e.preventDefault();
                    props.createPatientProfileTab(patient.id);
                  }
                }}
              >
                {isMergeEnabled && (
                  <TableCell onClick={(event: any) => event.stopPropagation()}>
                    <CheckBox
                      name={patient.id.toString()}
                      disabled={
                        (!selectedPatients.includes(patient.id) &&
                          selectedPatients.length === 2) ||
                        (!!selectedSubtenantId &&
                          patient.subtenantId !== selectedSubtenantId) ||
                        patient.isDeleted
                      }
                      isTooltipEnabled={
                        !!selectedSubtenantId &&
                        patient.subtenantId !== selectedSubtenantId
                      }
                      tooltipText={i18next.t(
                        'merge_enabled.disabled_checkbox_text',
                      )}
                      checked={selectedPatients.includes(patient.id)}
                      className={styles.checkbox}
                      onChange={() => {
                        if (selectedPatients.includes(patient.id)) {
                          setSelectedPatients(
                            selectedPatients.filter(
                              (patientId) => patientId !== patient.id,
                            ),
                          );
                        } else {
                          setSelectedPatients([
                            ...selectedPatients,
                            patient.id,
                          ]);
                        }
                      }}
                      testId="select-checkbox-for-searched-row"
                    />
                  </TableCell>
                )}
                <TableCell>
                  <div className={styles.patientIdCell}>
                    {patient.number}
                    {patient.numberSource === NumberSource.External && (
                      <ExternalPatientNumberBadge />
                    )}
                  </div>
                </TableCell>
                <TableCell>
                  <div className={styles.tableCell}>
                    {localizeSexType(patient.sex)}
                  </div>
                </TableCell>
                <TableCell>
                  <div className={styles.patientInfoCell}>
                    <div
                      className={styles.patientNameInfo}
                      data-test-id="patientTable-patientName"
                    >
                      {getFullName(patient)}
                    </div>
                    <div className={styles.patientSubtenantInfo}>
                      {subtenants[patient.subtenantId]?.code}
                    </div>
                  </div>
                </TableCell>
                <TableCell>
                  <div className={styles.tableCell}>
                    {getFormattedDateRange(
                      patient.dateOfBirth,
                      patient.dateOfDeath,
                    )}
                  </div>
                </TableCell>
                <TableCell>
                  <div className={styles.tableCell}>
                    {patient.priorityContact}
                  </div>
                </TableCell>
                {columnKeys.includes(PatientTableKeys.status) && (
                  <TableCell>
                    <div className={styles.tableCell}>
                      {i18next.t(
                        `statuses.${patient.isActive ? 'active' : 'deceased'}`,
                      )}
                    </div>
                  </TableCell>
                )}
                {columnKeys.includes(PatientTableKeys.lastVisit) && (
                  <TableCell>
                    <div className={styles.tableCell}>
                      {getFormattedDateTime(patient.lastVisitDateTime)}
                    </div>
                  </TableCell>
                )}
                {columnKeys.includes(PatientTableKeys.isDeleted) && (
                  <TableCell>
                    <div className={styles.tableCell}>
                      {i18next.t(
                        `states.${
                          patient.isDeleted
                            ? patient.anonymous
                              ? 'anonymous'
                              : 'deleted'
                            : 'active'
                        }`,
                      )}
                    </div>
                  </TableCell>
                )}
                <TableCell onClick={(event: any) => event.stopPropagation()}>
                  <div className={styles.optionCell}>
                    <TableDotMenu
                      options={[
                        {
                          title: i18next.t('options.start_miniscreen_online'),
                          onClick: () =>
                            openWithMiniscreen({
                              patientId: patient.id,
                              mode: MiniscreenMode.Online,
                            }),
                          isHidden:
                            patient.isDeleted ||
                            !isStartMiniscreenOnlineModeEnabled() ||
                            !claims.permissions.importMeasurements ||
                            !isMiniscreen(),
                          disabled:
                            !claims.permissions.importMeasurements ||
                            !isMiniscreen(),
                        },
                        {
                          title: i18next.t('options.start_miniscreen_offline'),
                          onClick: () =>
                            openWithMiniscreen({
                              patientId: patient.id,
                              mode: MiniscreenMode.Offline,
                            }),
                          isHidden:
                            patient.isDeleted ||
                            !isStartMiniscreenOfflineModeEnabled() ||
                            !claims.permissions.importMeasurements ||
                            !isMiniscreen(),
                          disabled:
                            !claims.permissions.importMeasurements ||
                            !isMiniscreen(),
                        },
                        {
                          title: i18next.t('options.restore_patient'),
                          onClick: () => props.restorePatient(patient.id),
                          isHidden:
                            !patient.isDeleted ||
                            patient.anonymous ||
                            !claims.permissions.patientDeleteAndRestore,
                        },
                        {
                          title: i18next.t('options.archive'),
                          onClick: () => props.removePatient(patient.id),
                          isDeleteItem: true,
                          isUnderLine: true,
                          isHidden:
                            patient.isDeleted ||
                            patient.anonymous ||
                            !claims.permissions.patientDeleteAndRestore,
                        },
                        {
                          title: i18next.t('options.permanently_delete'),
                          onClick: () =>
                            props.removePatientPermanently(
                              patient.id,
                              getFullName(patient),
                            ),
                          isDeleteItem: true,
                          isUnderLine: true,
                          isHidden:
                            !claims.permissions
                              .patientAnonymizeAndDeletePermanently,
                        },
                        {
                          title: i18next.t('options.anonymize'),
                          onClick: () =>
                            props.anonymizePatient(
                              patient.id,
                              getFullName(patient),
                            ),
                          isDeleteItem: true,
                          isUnderLine: true,
                          isHidden:
                            patient.anonymous ||
                            !claims.permissions
                              .patientAnonymizeAndDeletePermanently,
                        },
                      ]}
                    />
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    );
  },
);
